import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Video } from 'lucide-react';
import SEO from '@/components/SEO';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <SEO
        title="About Toonah — Anonymous Video Chat for Real Human Connections"
        description="Learn about Toonah — a private, anonymous, one-on-one video chat platform with no registration and ephemeral conversations."
        path="/about"
      />
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="about-back-home"
            className="flex items-center"
          >
            <img
              src="/toonah-wordmark.webp"
              alt="Toonah"
              className="h-7 sm:h-8 w-auto object-contain select-none"
              draggable={false}
            />
          </button>
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => navigate('/')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              Privacy
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="text-sm font-semibold hover:text-blue-600 transition-colors"
            >
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <article className="max-w-2xl mx-auto px-6 pt-32 pb-24" data-testid="about-page">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600 mb-4">
            Our Story
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            About Toonah
          </h1>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-6 text-lg leading-relaxed text-slate-700 dark:text-slate-300"
        >
          <p>Most of the internet asks us to present ourselves.</p>

          <p>
            Across social media and dating apps, we are constantly curating how we look,
            what we share, what we say, and how we want others to see us. Over time,
            connection can start to feel like performance.
          </p>

          <p className="text-slate-900 dark:text-white font-semibold">
            Toonah was created as an alternative.
          </p>

          <p>
            Before social media became part of everyday life, conversations were often
            simpler. People talked without profiles, follower counts, likes, or carefully
            curated identities. They shared stories, laughed, listened, disagreed, and
            sometimes connected simply because two people happened to have a conversation.
          </p>

          <p>Toonah brings a little of that spontaneity back to the internet.</p>

          <p>
            There are no profiles to build and no audience to impress. You meet one real
            person at a time, talk, decide when you want to reveal yourself, and move on
            whenever you choose.
          </p>

          <p className="text-slate-900 dark:text-white font-semibold text-xl">
            One person. One conversation. No audience.
          </p>

          <p>
            The goal is not to collect followers or prove anything. It is simply to have
            a real conversation.
          </p>

          <p>Less pressure. Less performance. More honest conversation.</p>

          <p>
            Toonah isn&rsquo;t social media. It&rsquo;s a place to meet one real person
            at a time.
          </p>

          <p className="text-slate-500 dark:text-slate-400 italic">
            That idea is where Toonah was born.
          </p>
        </motion.div>

        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800"
        >
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Created by{' '}
            <a
              href="https://www.linkedin.com/in/bsnirmalraj/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-creator-link"
              className="font-semibold text-slate-700 dark:text-slate-200 underline decoration-slate-300 dark:decoration-slate-600 underline-offset-4 hover:text-blue-600 hover:decoration-blue-600 transition-colors"
            >
              BS Nirmal Raj
            </a>
            .
          </p>
        </motion.footer>
      </article>
    </div>
  );
};

export default About;
